import ReplayIcon from '@mui/icons-material/Replay';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { ChipProps } from '@mui/material';
import { useEffect, useId, useRef, useState } from 'react';

import { CanvassingSessionRecordingWarning } from '../../graphql/__generated__/graphql';
import { CanvassingSessionTimestamp } from './CanvassingSessionTimestamp';
import type {
  CanvassingSessionTranscriptionFailureCode,
  CanvassingSessionTranscriptionPublication,
  CanvassingSessionTranscriptionResultBody,
  CanvassingSessionTranscriptionStatus,
  CanvassingSessionTranscriptionWarning,
} from './sessionTranscriptionTypes';
import type { CanvassingSessionTranscriptionState } from './useCanvassingSessionTranscription';
import {
  SESSION_RECORDING_COMPLETENESS_UNKNOWN_WARNING,
  SESSION_RECORDING_INCOMPLETE_WARNING,
} from './useCanvassingSessionRecording';

const CONFIRMATION_BODY = "Send the entire available session recording to Core for transcription and extraction using this account's slot configuration? The recording may be incomplete or contain gaps.";
const TRANSCRIPT_PAGE_SIZE = 200;
const RESERVED_SLOT_KEYS = new Set(['observation', 'observations', 'conversationalTags']);
const EMPTY_SLOT_RECORD: Record<string, unknown> = Object.create(null);

const STATUS_LABELS: Record<CanvassingSessionTranscriptionStatus, string> = {
  QUEUED: 'Queued',
  SUBMITTING: 'Submitting',
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
};

const STATUS_COLORS: Record<CanvassingSessionTranscriptionStatus, ChipProps['color']> = {
  QUEUED: 'default',
  SUBMITTING: 'info',
  PROCESSING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
};

export const transcriptionFailureCopy = (failureCode: CanvassingSessionTranscriptionFailureCode | null): string => {
  if (failureCode === 'CORE_CONFIGURATION_REQUIRED') return 'Core configuration is required.';
  if (failureCode === 'RECORDING_UNAVAILABLE' || failureCode === 'RECORDING_IDENTITY_INVALID') {
    return 'The session recording is unavailable.';
  }
  if (failureCode === 'ANALYSIS_TIMEOUT') return 'Core analysis has not completed within 24 hours. You can retry.';
  if (failureCode === 'SESSION_TRANSCRIPTION_IN_PROGRESS') return 'A transcription is already in progress for this session.';
  if (failureCode === 'CORE_REJECTED' || failureCode === 'CORE_RESPONSE_INVALID' || failureCode === 'SUBMISSION_FAILED') {
    return 'Transcription submission failed. You can retry.';
  }
  return 'Transcription failed. You can retry.';
};

const warningCopy = (warning: CanvassingSessionTranscriptionWarning): string =>
  warning === CanvassingSessionRecordingWarning.CompletenessUnknown
    ? SESSION_RECORDING_COMPLETENESS_UNKNOWN_WARNING
    : SESSION_RECORDING_INCOMPLETE_WARNING;

const statusName = (status: CanvassingSessionTranscriptionStatus | undefined): string =>
  status === undefined ? 'Not started' : STATUS_LABELS[status];

const actionName = (latestStatus: CanvassingSessionTranscriptionStatus | undefined): string => {
  if (latestStatus === 'QUEUED' || latestStatus === 'SUBMITTING' || latestStatus === 'PROCESSING') return 'Transcription in progress';
  if (latestStatus === 'FAILED') return 'Retry transcription';
  if (latestStatus === 'COMPLETED') return 'Transcribe again';
  return 'Transcribe session';
};

const disableReason = ({
  sessionActive,
  hasMergedRecording,
  publication,
}: {
  sessionActive: boolean;
  hasMergedRecording: boolean | null | undefined;
  publication: CanvassingSessionTranscriptionPublication | null;
}): string | null => {
  if (sessionActive || publication?.eligibility === 'SESSION_ACTIVE') return 'Transcription is unavailable while this session is active.';
  if (hasMergedRecording === false || publication?.eligibility === 'RECORDING_NOT_MERGED') {
    return 'Transcription is unavailable because no merged session recording is available.';
  }
  const latestStatus = publication?.latestRun?.status;
  if (publication?.eligibility === 'IN_PROGRESS' || latestStatus === 'QUEUED' || latestStatus === 'SUBMITTING' || latestStatus === 'PROCESSING') {
    return 'Transcription in progress.';
  }
  if (publication?.enabled === false || publication?.eligibility === 'DISABLED') return 'Session transcription is operationally disabled.';
  if (hasMergedRecording !== true && publication?.eligibility !== 'ELIGIBLE') {
    return 'Transcription is unavailable because recording eligibility could not be confirmed.';
  }
  return null;
};

export const CanvassingSessionTranscriptionAction = ({
  state,
  sessionActive,
  hasMergedRecording,
  submit,
}: {
  state: CanvassingSessionTranscriptionState;
  sessionActive: boolean;
  hasMergedRecording: boolean | null | undefined;
  submit: () => Promise<boolean>;
}) => {
  const dialogId = useId();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const actionButtonRef = useRef<HTMLButtonElement | null>(null);
  const actionRegionRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef(false);
  const submissionInFlightRef = useRef(false);
  const publication = state.status === 'ready' ? state.publication : state.status === 'error' ? state.publication : null;
  const latestRun = publication?.latestRun ?? null;
  const latestStatus = latestRun?.status;
  const label = actionName(latestStatus);
  const reason = state.status === 'deadline'
    ? 'Analysis is still running. Refresh to check for updates.'
    : disableReason({ sessionActive, hasMergedRecording, publication });
  const isInProgress = state.status === 'deadline' || latestStatus === 'QUEUED' || latestStatus === 'SUBMITTING' || latestStatus === 'PROCESSING'
    || publication?.eligibility === 'IN_PROGRESS';
  const previousFailure = latestStatus === 'FAILED'
    || state.status === 'error' && (state.uncertainSubmission || state.authoritativeSubmissionFailure);
  const uncertainSubmission = state.status === 'error' && state.uncertainSubmission;
  const authoritativeSubmissionFailure = state.status === 'error' && state.authoritativeSubmissionFailure;
  const submissionAlreadyInProgress = state.status === 'error' && state.error.failureCode === 'SESSION_TRANSCRIPTION_IN_PROGRESS';
  const startsNewAnalysis = !uncertainSubmission && !submissionAlreadyInProgress
    && (latestStatus === 'COMPLETED' || latestStatus === 'FAILED' || authoritativeSubmissionFailure);
  const hasPriorResult = publication?.result !== null && publication?.result !== undefined;
  const confirmationDescriptionIds = [
    `${dialogId}-description`,
    ...(hasPriorResult && startsNewAnalysis ? [`${dialogId}-prior-result`] : []),
    ...(previousFailure ? [`${dialogId}-retry-description`] : []),
  ];

  const close = (): void => {
    if (submissionInFlightRef.current) return;
    restoreFocusRef.current = true;
    setDialogOpen(false);
  };

  const confirm = async (): Promise<void> => {
    if (submissionInFlightRef.current) return;
    submissionInFlightRef.current = true;
    setSubmissionError(false);
    setSubmitting(true);
    try {
      if (await submit()) {
        restoreFocusRef.current = true;
        setDialogOpen(false);
      } else if (stateRef.current.status === 'forbidden') {
        restoreFocusRef.current = true;
        setSubmissionError(false);
        setDialogOpen(false);
      } else {
        setSubmissionError(true);
      }
    } catch {
      setSubmissionError(true);
    } finally {
      submissionInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  if (state.status === 'forbidden') return null;

  return (
    <>
      <Box ref={actionRegionRef} role="group" aria-label="Session transcription action" tabIndex={-1}>
        <Button
          variant="outlined"
          ref={actionButtonRef}
          startIcon={submitting ? <CircularProgress size={16} aria-hidden /> : <ReplayIcon />}
          onClick={() => setDialogOpen(true)}
          disabled={reason !== null || isInProgress || submitting || state.status === 'loading'}
          aria-busy={submitting}
          aria-describedby={reason === null ? undefined : `${dialogId}-disabled-reason`}
        >
          {isInProgress || submitting ? 'Transcription in progress' : label}
        </Button>
        {reason !== null && (
          <Typography id={`${dialogId}-disabled-reason`} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {reason}
          </Typography>
        )}
      </Box>
      <Dialog
        open={dialogOpen}
        onClose={close}
        aria-labelledby={`${dialogId}-title`}
        aria-describedby={confirmationDescriptionIds.join(' ')}
        aria-busy={submitting}
        slotProps={{
          paper: { 'aria-busy': submitting },
          transition: {
            onExited: () => {
              if (!restoreFocusRef.current) return;
              restoreFocusRef.current = false;
              if (actionButtonRef.current?.disabled) actionRegionRef.current?.focus();
              else actionButtonRef.current?.focus();
            },
          },
        }}
      >
        <DialogTitle id={`${dialogId}-title`}>{label}</DialogTitle>
        <DialogContent>
          <DialogContentText id={`${dialogId}-description`}>{CONFIRMATION_BODY}</DialogContentText>
          {hasPriorResult && startsNewAnalysis && (
            <DialogContentText id={`${dialogId}-prior-result`} sx={{ mt: 1 }}>
              The previous result will remain visible until the new analysis completes.
            </DialogContentText>
          )}
          {previousFailure && (
            <DialogContentText id={`${dialogId}-retry-description`} sx={{ mt: 1 }}>
              {uncertainSubmission
                ? 'Core may have accepted the previous request. Confirm resends the same request.'
                : 'The previous attempt failed. Confirm starts a new analysis.'}
            </DialogContentText>
          )}
          {submissionError && <Alert severity="error" role="status" aria-live="polite" sx={{ mt: 2 }}>Transcription submission failed. You can retry.</Alert>}
          {submitting && (
            <Stack role="status" aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center', mt: 2 }}>
              <CircularProgress size={20} aria-hidden />
              <Typography variant="body2">Submitting transcription request…</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={close} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={() => void confirm()} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Confirm transcription'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const statusForState = (state: CanvassingSessionTranscriptionState): CanvassingSessionTranscriptionStatus | undefined =>
  state.status === 'ready' || state.status === 'error' ? state.publication?.latestRun?.status : undefined;

const displayedPublication = (state: CanvassingSessionTranscriptionState): CanvassingSessionTranscriptionPublication | null =>
  state.status === 'ready' || state.status === 'error' ? state.publication : null;

const safeRequestErrorCopy = (failureCode: string | null): string => {
  if (failureCode === 'SESSION_TRANSCRIPTION_IN_PROGRESS') return transcriptionFailureCopy(failureCode);
  if (failureCode === 'CORE_CONFIGURATION_REQUIRED' || failureCode === 'RECORDING_UNAVAILABLE'
    || failureCode === 'RECORDING_IDENTITY_INVALID' || failureCode === 'CORE_REJECTED'
    || failureCode === 'CORE_RESPONSE_INVALID' || failureCode === 'SUBMISSION_FAILED' || failureCode === 'ANALYSIS_TIMEOUT') {
    return transcriptionFailureCopy(failureCode);
  }
  return 'Unable to load session analysis. Refresh to try again.';
};

const normalizedTextList = (values: readonly string[]): string[] => values
  .filter((value): value is string => typeof value === 'string')
  .map(value => value.trim())
  .filter(value => value.length > 0);

const isHttpUrl = (value: string | null | undefined): value is string => {
  if (!value?.trim() || !/^https?:\/\//i.test(value.trim())) return false;

  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.host.length > 0;
  } catch {
    return false;
  }
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const slotValueText = (value: unknown): string => {
  if (value === null) return 'null';
  if (Array.isArray(value) || typeof value === 'object') return JSON.stringify(value, null, 2) ?? 'null';
  return String(value);
};

const renderAnalysisResult = (result: CanvassingSessionTranscriptionResultBody) => {
  const observations = normalizedTextList(Array.isArray(result.observations) ? result.observations : []);
  const tags = normalizedTextList(Array.isArray(result.conversationalTags) ? result.conversationalTags : []);
  const extractedSlots = isPlainRecord(result.extractedSlots) ? result.extractedSlots : EMPTY_SLOT_RECORD;
  const slots = Object.keys(extractedSlots)
    .filter(key => !RESERVED_SLOT_KEYS.has(key))
    .sort();

  return (
    <Stack spacing={2.5}>
      <TranscriptSection result={result} />
      <Box component="section" aria-labelledby="session-analysis-observations">
        <Typography id="session-analysis-observations" variant="body1" sx={{ fontWeight: 600, mb: 1 }}>Observations</Typography>
        {observations.length === 0 ? <Typography variant="body2" color="text.secondary">No observations returned</Typography> : (
          <Box component="ul" sx={{ m: 0, pl: 3, overflowWrap: 'anywhere' }}>
            {observations.map((text, index) => <li key={`${index}-${text}`}><Typography component="span" variant="body2">{text}</Typography></li>)}
          </Box>
        )}
      </Box>
      <Box component="section" aria-labelledby="session-analysis-tags">
        <Typography id="session-analysis-tags" variant="body1" sx={{ fontWeight: 600, mb: 1 }}>Conversational tags</Typography>
        {tags.length === 0 ? <Typography variant="body2" color="text.secondary">No conversational tags returned</Typography> : (
          <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: 'wrap' }}>
            {tags.map((tag, index) => <Chip key={`${index}-${tag}`} label={tag} size="small" variant="outlined" sx={{ maxWidth: '100%', '& .MuiChip-label': { overflowWrap: 'anywhere', whiteSpace: 'normal' } }} />)}
          </Stack>
        )}
      </Box>
      <Box component="section" aria-labelledby="session-analysis-slots">
        <Typography id="session-analysis-slots" variant="body1" sx={{ fontWeight: 600, mb: 1 }}>Extracted slots</Typography>
        {slots.length === 0 ? <Typography variant="body2" color="text.secondary">No extracted slots returned</Typography> : (
          <Box component="dl" sx={{ m: 0 }}>
            {slots.map(key => {
              const slot = Reflect.get(extractedSlots, key);
              const value = slotValueText(slot);
              const structured = slot !== null && (Array.isArray(slot) || typeof slot === 'object');
              return (
                <Stack
                  key={key}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider', overflowWrap: 'anywhere' }}
                >
                  <Typography component="dt" variant="body2" sx={{ flex: '0 0 32%', fontWeight: 600, overflowWrap: 'anywhere' }}>{key}</Typography>
                  {structured ? (
                    <Box component="dd" sx={{ m: 0, minWidth: 0, maxWidth: '100%', overflowX: 'auto' }}>
                      <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontFamily: 'monospace' }}>{value}</Box>
                    </Box>
                  ) : (
                    <Typography component="dd" variant="body2" sx={{ m: 0, minWidth: 0, overflowWrap: 'anywhere' }}>{value}</Typography>
                  )}
                </Stack>
              );
            })}
          </Box>
        )}
      </Box>
    </Stack>
  );
};

const TranscriptSection = ({ result }: { result: CanvassingSessionTranscriptionResultBody }) => {
  const [page, setPage] = useState(0);
  const identity = `${result.runId}:${result.revision}`;
  useEffect(() => setPage(0), [identity]);
  const transcript = Array.isArray(result.transcript) ? result.transcript : [];
  const total = transcript.length;
  const pageCount = Math.max(1, Math.ceil(total / TRANSCRIPT_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const entries = transcript.slice(currentPage * TRANSCRIPT_PAGE_SIZE, (currentPage + 1) * TRANSCRIPT_PAGE_SIZE);
  const start = total === 0 ? 0 : currentPage * TRANSCRIPT_PAGE_SIZE + 1;
  const end = Math.min(total, (currentPage + 1) * TRANSCRIPT_PAGE_SIZE);

  return (
    <Box component="section" aria-labelledby="session-analysis-transcript">
      <Typography id="session-analysis-transcript" variant="body1" sx={{ fontWeight: 600, mb: 1 }}>Transcript</Typography>
      {total === 0 ? <Typography variant="body2" color="text.secondary">No transcript returned</Typography> : (
        <>
          <Stack spacing={1}>
            {entries.map((entry, index) => {
              const speaker = typeof entry.speaker === 'string' && entry.speaker.trim() ? entry.speaker.trim() : 'Unknown speaker';
              const itemIndex = currentPage * TRANSCRIPT_PAGE_SIZE + index;
              const text = typeof entry.text === 'string' ? entry.text : '';
              return (
                <Typography key={`${identity}-${itemIndex}`} variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  <Box component="strong">{speaker}: </Box>{text}
                </Typography>
              );
            })}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 0}>Previous transcript page</Button>
            <Button variant="outlined" size="small" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= pageCount - 1}>Next transcript page</Button>
            <Typography variant="body2" color="text.secondary">Showing {start}–{end} of {total} transcript entries</Typography>
          </Stack>
        </>
      )}
    </Box>
  );
};

export const CanvassingSessionTranscriptionSection = ({
  state,
  refresh,
}: {
  state: CanvassingSessionTranscriptionState;
  refresh: () => void;
}) => {
  const publication = displayedPublication(state);
  const latestRun = publication?.latestRun ?? null;
  const coreCallId = latestRun?.coreCallId?.trim() ?? '';
  const coreLeadId = latestRun?.coreLeadId?.trim() ?? '';
  const runCoreLeadUrl = latestRun?.coreLeadUrl;
  const coreLeadUrl = isHttpUrl(runCoreLeadUrl) ? runCoreLeadUrl : null;
  const result = publication?.result ?? null;
  const latestStatus = statusForState(state);
  const active = latestStatus === 'QUEUED' || latestStatus === 'SUBMITTING' || latestStatus === 'PROCESSING';
  const previousResult = result !== null && latestRun !== null && latestRun.id !== publication?.publishedRunId
    && (active || latestStatus === 'FAILED');
  const pendingLoading = state.status === 'loading';

  return (
    <Paper component="section" variant="outlined" aria-labelledby="session-analysis-heading" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
          <Typography id="session-analysis-heading" variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>Session analysis</Typography>
          <Button variant="outlined" onClick={refresh} disabled={pendingLoading || state.status === 'forbidden'}>Refresh analysis</Button>
        </Stack>
        <Box aria-live="polite" aria-atomic="true">
          {pendingLoading && <Typography variant="body2" color="text.secondary">Loading session analysis…</Typography>}
          {state.status === 'forbidden' && <Typography variant="body2" color="text.secondary">Session analysis is unavailable.</Typography>}
          {state.status === 'unavailable' && <Typography variant="body2" color="text.secondary">Session analysis is unavailable for this session.</Typography>}
          {state.status === 'deadline' && <Typography variant="body2" color="text.secondary">Analysis is still running. Refresh to check for updates.</Typography>}
          {state.status === 'ready' && latestRun === null && result === null && (
            <Typography variant="body2" color="text.secondary">This session has not been transcribed.</Typography>
          )}
          {state.status === 'ready' && latestRun !== null && (
            <Typography variant="body2">Status: {statusName(latestRun.status)}</Typography>
          )}
          {state.status === 'ready' && state.pollingTimedOut && active && (
            <Typography variant="body2" color="text.secondary">Analysis is still running. Refresh to check for updates.</Typography>
          )}
        </Box>
        {state.status === 'ready' && latestRun !== null && (
          <Chip label={STATUS_LABELS[latestRun.status]} size="small" color={STATUS_COLORS[latestRun.status]} variant="outlined" />
        )}
        {latestRun?.requestedAt && <Typography variant="body2" color="text.secondary">Requested <CanvassingSessionTimestamp timestamp={latestRun.requestedAt} /></Typography>}
        {latestRun?.completedAt && <Typography variant="body2" color="text.secondary">Latest run completed <CanvassingSessionTimestamp timestamp={latestRun.completedAt} /></Typography>}
        {(coreLeadUrl !== null || coreLeadId !== '' || coreCallId !== '') && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap' }}>
            {coreLeadUrl !== null ? (
              <MuiLink href={coreLeadUrl} target="_blank" rel="noopener noreferrer">View Core lead</MuiLink>
            ) : coreLeadId !== '' ? (
              <Typography variant="body2">Core lead ID: {coreLeadId}</Typography>
            ) : null}
            {coreCallId !== '' && <Typography variant="body2">Core call ID: {coreCallId}</Typography>}
          </Stack>
        )}
        {latestRun?.status === 'FAILED' && <Alert severity="error" role="status" aria-live="polite">{transcriptionFailureCopy(latestRun.failureCode ?? null)}</Alert>}
        {state.status === 'error' && <Alert severity="error" role="status" aria-live="polite">{safeRequestErrorCopy(state.error.failureCode)}</Alert>}
        {publication?.enabled === false && <Typography variant="body2" color="text.secondary">Session transcription is operationally disabled.</Typography>}
        {latestRun?.warnings.map((warning, index) => (
          <Alert severity="warning" key={`${warning}-${index}`}>{warningCopy(warning)}</Alert>
        ))}
        {previousResult && result !== null && (
          <Alert severity="info" role="status" aria-live="polite">
            Showing the previous completed analysis{result.completedAt ? <> from <CanvassingSessionTimestamp timestamp={result.completedAt} /></> : ''}.
          </Alert>
        )}
        {result !== null && renderAnalysisResult(result)}
        {state.status === 'ready' && latestRun?.status === 'COMPLETED' && result === null && (
          <Typography variant="body2" color="text.secondary">The completed analysis has no published result.</Typography>
        )}
      </Stack>
    </Paper>
  );
};
